import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useIsPhone } from '../../hooks/useIsPhone';

import { supabase } from '../../lib/supabase';
import { AuthPage } from '../auth/AuthPage';
import { AuthCallback } from '../auth/AuthCallback';
import { ForgotPassword } from '../auth/ForgotPassword';
import Dashboard from '../pages/Dashboard';
import { MobileRouter } from '../mobile/MobileRouter';

import { OnboardingFlow } from '../pages/OnboardingFlow';
import SettingsDashboard from '../Settings/SettingsDashboard';
import InvitationAcceptance from '../TrustedAdvisorManagement/InvitationAcceptance';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { ComingSoonPage } from '../pages/ComingSoonPage';
import { ScoreScreen } from '../pages/ScoreScreen';

// Mobile redirect wrapper component
const MobileRedirectWrapper = ({ children, redirectTo = '/mobile' }: { children: React.ReactNode, redirectTo?: string }) => {
  const isPhone = useIsPhone();
  
  if (isPhone) {
    return <Navigate to={redirectTo} replace />;
  }
  
  return <>{children}</>;
};

// Mobile-only wrapper component
const MobileOnlyWrapper = ({ children }: { children: React.ReactNode }) => {
  const isPhone = useIsPhone();
  
  if (!isPhone) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-idecide-primary mx-auto mb-4"></div>
      <p className="text-gray-600">Loading your session...</p>
    </div>
  </div>
);

const ProfileErrorScreen = ({ error, onRetry, onSignOut }: { error: string; onRetry: () => void; onSignOut: () => void }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to load your profile</h2>
      <p className="text-gray-600 text-sm mb-6">{error}</p>
      <div className="flex flex-col gap-3">
        <button
          onClick={onRetry}
          className="w-full bg-idecide-primary text-white font-semibold rounded-lg py-3 hover:bg-idecide-primary-hover transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={onSignOut}
          className="w-full border border-gray-300 text-gray-700 font-semibold rounded-lg py-3 hover:bg-gray-50 transition-colors"
        >
          Sign Out
        </button>
      </div>
    </div>
  </div>
);

const AppRouter = function AppRouter() {
  const { user, loading, profile, profileError, retryProfileFetch, refreshProfile } = useAuth();
  const [hasCheckedExistingUser, setHasCheckedExistingUser] = useState(false);
  const isPhone = useIsPhone();

  // For existing users whose profile has onboarding_completed=false,
  // check if they have data in the app and auto-complete onboarding.
  useEffect(() => {
    const checkExistingUser = async () => {
      if (!user || !profile || profile.onboarding_completed || hasCheckedExistingUser) return;

      try {
        const checks = await Promise.allSettled([
          supabase.from('calendar_events').select('id').eq('user_id', user.id).limit(1),
          supabase.from('pets').select('id').eq('user_id', user.id).limit(1),
          supabase.from('legal_documents').select('id').eq('user_id', user.id).limit(1),
          supabase.from('financial_accounts').select('id').eq('user_id', user.id).limit(1),
          supabase.from('health_records').select('id').eq('user_id', user.id).limit(1),
          supabase.from('travel_bookings').select('id').eq('user_id', user.id).limit(1),
          supabase.from('ndis_goals').select('id').eq('user_id', user.id).limit(1),
          supabase.from('trusted_advisors').select('id').eq('user_id', user.id).limit(1),
          supabase.from('digital_estate_contacts').select('id').eq('user_id', user.id).limit(1),
          supabase.from('legacy_journals').select('id').eq('user_id', user.id).limit(1),
        ]);

        const hasData = checks.some(result =>
          result.status === 'fulfilled' &&
          result.value.data &&
          result.value.data.length > 0
        );

        if (hasData) {
          await supabase
            .from('profiles')
            .update({ onboarding_completed: true })
            .eq('id', user.id);

          // Refresh the profile in React state so the router picks up the change
          await refreshProfile();
        }

        setHasCheckedExistingUser(true);
      } catch (error) {
        console.error('Error checking existing user:', error);
        setHasCheckedExistingUser(true);
      }
    };

    checkExistingUser();
  }, [user, profile, hasCheckedExistingUser, refreshProfile]);

  // Show loading screen while auth is initializing OR while we have a user
  // but the profile hasn't loaded yet (prevents flash of onboarding page).
  if (loading || (user && !profile && !profileError)) {
    return <LoadingScreen />;
  }

  // Profile failed to load after retries — show a recoverable error screen instead of
  // spinning forever (the previous behaviour).
  if (user && !profile && profileError) {
    return (
      <ProfileErrorScreen
        error={profileError}
        onRetry={retryProfileFetch}
        onSignOut={async () => { await supabase.auth.signOut(); window.location.href = '/auth'; }}
      />
    );
  }

  return (
    <Router>
      <Routes>
        {/* Auth routes */}
        <Route
          path="/auth"
          element={
            user ? (
              profile?.onboarding_completed ? (
                isPhone ? (
                  <Navigate to="/mobile" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <Navigate to="/onboarding" replace />
              )
            ) : (
              <AuthPage />
            )
          }
        />

        {/* Auth callback for OAuth and magic links */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Password reset route */}
        <Route
          path="/auth/reset-password"
          element={<ForgotPassword onBack={() => { window.location.href = '/auth'; }} />}
        />

        {/* Onboarding route */}
        <Route
          path="/onboarding"
          element={
            user ? (
              profile?.onboarding_completed ? (
                isPhone ? (
                  <Navigate to="/mobile" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <ErrorBoundary
                  fallback={
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
                        <p className="text-gray-600 mb-4">We encountered an error during onboarding.</p>
                        <button
                          onClick={() => window.location.reload()}
                          className="bg-idecide-primary text-white px-4 py-2 rounded-lg hover:bg-idecide-primary-hover"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  }
                >
                  <OnboardingFlow />
                </ErrorBoundary>
              )
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />



        {/* Mobile routes */}
        <Route
          path="/mobile/*"
          element={
            user ? (
              profile?.onboarding_completed ? (
                <MobileOnlyWrapper>
                  <MobileRouter />
                </MobileOnlyWrapper>
              ) : (
                <Navigate to="/onboarding" replace />
              )
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />

        <Route
          path="/dashboard/*"
          element={
            user ? (
              profile?.onboarding_completed ? (
                <MobileRedirectWrapper redirectTo="/mobile">
                  <Dashboard />
                </MobileRedirectWrapper>
              ) : (
                <Navigate to="/onboarding" replace />
              )
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />


        {/* Payment routes — paywall removed, redirect to dashboard */}
        <Route path="/payment" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payment/success" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payment/cancel" element={<Navigate to="/dashboard" replace />} />

        {/* Score screen route */}
        <Route
          path="/score"
          element={
            user ? (
              <MobileRedirectWrapper redirectTo="/mobile">
                <ScoreScreen />
              </MobileRedirectWrapper>
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />

        {/* Coming soon page */}
        <Route
          path="/coming-soon"
          element={<ComingSoonPage />}
        />

        {/* Trusted Advisor Invitation Route */}
        <Route
          path="/trusted-advisor/invite/:token"
          element={<InvitationAcceptance />}
        />

        {/* Default redirect */}
        <Route
          path="/"
          element={
            user ? (
              profile?.onboarding_completed ? (
                isPhone ? (
                  <Navigate to="/mobile" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <Navigate to="/onboarding" replace />
              )
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />

        {/* Catch all route */}
        <Route
          path="*"
          element={
            user ? (
              profile?.onboarding_completed ? (
                isPhone ? (
                  <Navigate to="/mobile" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <Navigate to="/onboarding" replace />
              )
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
};

export { AppRouter };